import React from "react";
import {
  Timeline,
  Text,
  Paper,
  UnstyledButton,
  Group,
  ThemeIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconClock,
  IconCircleDot,
} from "@tabler/icons";
import moment from "moment";
import McaStatusBadge from "./McaStatusBadge";

const getStepColor = (step, idx, currentStep, requestStatus) => {
  if (step.status === "APPROVED") return "green";
  if (step.status === "REJECTED") return "red";
  if (
    idx === currentStep &&
    ["PENDING", "IN_PROGRESS"].includes(requestStatus)
  ) {
    return "teal";
  }
  return "gray";
};

const getStepBullet = (step, idx, currentStep, requestStatus) => {
  if (step.status === "APPROVED") {
    return (
      <ThemeIcon color="green" radius="xl" size={24}>
        <IconCheck size={14} />
      </ThemeIcon>
    );
  }
  if (step.status === "REJECTED") {
    return (
      <ThemeIcon color="red" radius="xl" size={24}>
        <IconX size={14} />
      </ThemeIcon>
    );
  }
  if (
    idx === currentStep &&
    ["PENDING", "IN_PROGRESS"].includes(requestStatus)
  ) {
    return (
      <ThemeIcon color="teal" radius="xl" size={24}>
        <IconCircleDot size={14} />
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon color="gray" variant="outline" radius="xl" size={24}>
      <IconClock size={14} />
    </ThemeIcon>
  );
};

const McaTimeline = ({
  request,
  selectedStep = 0,
  onSelectStep,
}) => {
  const steps = request?.stepResults ?? [];
  const currentStep = request?.currentStep ?? 0;
  const requestStatus = request?.status;

  if (!steps.length) {
    return <Text color="dimmed">No approval steps configured.</Text>;
  }

  return (
    <Paper withBorder p="md" radius="sm">
      <Timeline active={steps.length} bulletSize={24} lineWidth={2}>
        {steps.map((step, idx) => {
          const isSelected = selectedStep === idx;
          const color = getStepColor(step, idx, currentStep, requestStatus);
          const showActionTimestamp =
            step.actionAt &&
            (step.status === "APPROVED" || step.status === "REJECTED");
          const description = showActionTimestamp
            ? `${step.actionBy?.name ?? step.actionBy?.cname ?? "Approver"} · ${moment(step.actionAt).format("YYYY-MM-DD HH:mm")}`
            : idx === currentStep &&
                ["PENDING", "IN_PROGRESS"].includes(requestStatus)
              ? "Awaiting approval"
              : "Pending";

          return (
            <Timeline.Item
              key={idx}
              bullet={getStepBullet(step, idx, currentStep, requestStatus)}
              color={color}
              title={
                <UnstyledButton
                  onClick={() => onSelectStep?.(idx)}
                  style={{ width: "100%", textAlign: "left" }}
                >
                  <Paper
                    p="xs"
                    radius="sm"
                    withBorder={isSelected}
                    sx={(theme) =>
                      isSelected
                        ? {
                            borderColor: theme.colors.teal[6],
                            backgroundColor:
                              theme.colorScheme === "dark"
                                ? theme.colors.dark[6]
                                : theme.colors.teal[0],
                          }
                        : {}
                    }
                  >
                    <Text size="sm" weight={isSelected ? 700 : 600}>
                      {step.name ?? `Step ${idx + 1}`}
                    </Text>
                    <Text size="xs" color="dimmed" mt={2}>
                      {description}
                    </Text>
                    <Group mt={4} spacing="xs">
                      <McaStatusBadge status={step.status} />
                    </Group>
                  </Paper>
                </UnstyledButton>
              }
            />
          );
        })}
      </Timeline>
    </Paper>
  );
};

export default McaTimeline;
